import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  MobileStepper,
  Paper,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CustomerData,
  ProjectDescriptionData,
  ProjectKeyData,
  ProjectStatus,
  ProjectUpdateDto,
} from "../../types/projectTypes";
import KeyDataStep from "../../components/projects/KeyDataStep";
import CustomerStep from "../../components/projects/CustomerStep";
import ProjectDescriptionStep from "../../components/projects/ProjectDescriptionStep";
import { showErrorMessage } from "../../utils/toastUtils";
import { useNavigate, useParams } from "react-router-dom";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useResponsive from "../../hooks/useResponsive";
import EditButtons from "../../components/projects/projectDetails/EditButtons";
import ProjectApplications from "../../components/projects/projectDetails/ProjectApplications";
import ProjectData from "../../components/projects/projectDetails/ProjectData";
import ProjectCompletionNoteCard from "../../components/projects/ProjectCompletionNoteCard";
import ProjectChip from "../../components/projects/projectCard/ProjectChip";
import { Groups, KeyboardArrowLeft, KeyboardArrowRight, Payments } from "@mui/icons-material";
import ChangeStatusButtons from "../../components/projects/projectDetails/ChangeStatusButtons";
import { validateCustomerData, validateProjectDescriptionData } from "../../utils/projectTenderingValidationUtils";
import useProjectsDetails from "../../hooks/projects/useProjectsDetails";
import LoadingCircle from "../../components/general/LoadingCircle";
import {
  mapToCustomerData,
  mapToProjectDescriptionData,
  mapToProjectKeyData,
  validateProjectKeyData,
} from "../../utils/projectUtils";
import useProjects from "../../hooks/projects/useProjects";
import dayjs, { Dayjs } from "dayjs";
import ConfirmationDialog from "../../components/general/ConfirmationDialog";
import EditProjectStaffingCommitteeDialog from "../../components/projects/EditProjectStaffingCommitteeDialog";
import { MembersFieldDto } from "../../types/membersTypes";
import useDepartments from "../../hooks/members/useDepartments";
import useMembers from "../../hooks/members/useMembers";
import StartSecondApplicationPhaseDialog from "../../components/projects/projectDetails/StartSecondApplicationPhaseDialog";
import { hasDayPassed } from "../../utils/dateUtils";
import { isApplicationPhaseStatus, PROJECT_COMPLETION_NOTE_MIN_LENGTH } from "../../utils/projectStatusUtils";

// Steps for the project details form
const steps = ["Rahmendaten", "Kundendaten", "Projektbeschreibung"];

/**
 * Component to display the project details
 * @returns ProjectDetails component
 */
const ProjectDetails = () => {
  const { id } = useParams();
  const {
    projectDetails,
    isProjectDetailsLoading,
    updateProject,
    updateProjectData,
    callOffProject,
    startTendering,
    offerAccepted,
    offerRejected,
    pitchLost,
    withdrawApplication,
    saveStaffingCommittee,
    startSecondApplicationPhase,
    updateProjectCompletionNote,
  } = useProjectsDetails(Number(id));
  const { currentDirectors } = useDepartments();
  const { members } = useMembers();
  const { checkHasPlQualification, checkHasQMQualification, allCoreCompetencies } = useProjects();
  const [isEditMode, setIsEditMode] = useState(false);
  const { auth } = useContext(AuthContext);
  const hasProjectPermission = doesPermissionsHaveSomeOf(auth.permissions, [19]);
  const hasProjectBillingPermission = doesPermissionsHaveSomeOf(auth.permissions, [22]);
  const hasUserApplied = projectDetails?.members?.some((member) => member.memberId === auth.userID);
  const isMobile = useResponsive("down", "sm");
  const theme = useTheme();
  const isApplicationOpen = isApplicationPhaseStatus(projectDetails?.status ?? "")
    ? projectDetails?.applicationEnd2
      ? (!projectDetails.applicationStart2 || dayjs(projectDetails.applicationStart2).isBefore(dayjs())) &&
        !hasDayPassed(projectDetails.applicationEnd2)
      : projectDetails?.applicationEnd1
      ? (!projectDetails.applicationStart1 || dayjs(projectDetails.applicationStart1).isBefore(dayjs())) &&
        !hasDayPassed(projectDetails.applicationEnd1)
      : false
    : false;
  const isSecondApplicationPhase = projectDetails?.applicationEnd2 != null;
  const navigate = useNavigate();
  const isPL =
    projectDetails?.members?.some((member) => member.memberId === auth.userID && member.type === "PL") || false;

  const isUserProjectMember = projectDetails?.members?.some((member) => member.memberId === auth.userID);
  const isUserQm = projectDetails?.qms?.some((qm) => qm.memberId === auth.userID);
  const showBillingButton =
    (projectDetails?.status === "Durchführung" ||
      projectDetails?.status === "Abrechnung" ||
      projectDetails?.status === "Abgeschlossen") &&
    (isUserProjectMember || isUserQm || hasProjectBillingPermission);

  const isUserInStaffingCommittee = projectDetails?.staffingCommittee.some((member) => member.memberId === auth.userID);

  // Project key data initial state
  const [projectKeyData, setProjectKeyData] = useState<ProjectKeyData>(() =>
    mapToProjectKeyData(projectDetails ?? undefined)
  );

  // Errors for the project key data inital state
  const [projectKeyDataErrors, setProjectKeyDataErrors] = React.useState<{ [key: string]: boolean }>({
    projectName: false,
    estimatedProjectStart: false,
    estimatedProjectDuration: false,
    estimatedProjectEuroPerBT: false,
    estimatedProjectBTmin: false,
    estimatedProjectBTmax: false,
    estimatedProjectMemberMin: false,
    estimatedProjectMemberMax: false,
    applicationStart1: false,
    applicationEnd1: false,
    applicationStart2: false,
    applicationEnd2: false,
  });

  // Customer data initial state
  const [customerData, setCustomerData] = useState<CustomerData>(() => mapToCustomerData(projectDetails ?? undefined));

  // Errors for the customer data initial state
  const [customerDataErrors, setCustomerDataErrors] = React.useState<{ [key: string]: boolean }>({
    customer: false,
    industry: false,
    newCustomer: false,
    contactPerson: false,
    acquisitor: false,
  });

  // Project Description data initial state
  const [projectDescriptionData, setProjectDescriptionData] = useState<ProjectDescriptionData>(() =>
    mapToProjectDescriptionData(projectDetails ?? undefined)
  );
  const [projectCompletionNoteDraft, setProjectCompletionNoteDraft] = useState(
    projectDetails?.projectCompletionNote ?? ""
  );
  const [isSavingProjectCompletionNote, setIsSavingProjectCompletionNote] = useState(false);

  // Errors for the project description data initial state
  const [projectDescriptionDataErrors, setProjectDescriptionDataErrors] = React.useState<{ [key: string]: boolean }>({
    coreCompetencies: false,
  });

  const [openStartSecondApplicationPhaseDialog, setOpenStartSecondApplicationPhaseDialog] = React.useState(false);
  const [startSecondApplicationPhaseStartDate, setStartSecondApplicationPhaseStartDate] = React.useState<Dayjs | null>(
    null
  );
  const [startSecondApplicationPhaseEndDate, setStartSecondApplicationPhaseEndDate] = React.useState<Dayjs | null>(
    null
  );
  const [startSecondApplicationPhaseStartDateInFutureError, setStartSecondApplicationPhaseStartDateInFutureError] =
    React.useState<boolean>(false);
  const [startSecondApplicationPhaseStartBeforeEndError, setStartSecondApplicationPhaseStartBeforeEndError] =
    React.useState<boolean>(false);
  const [startSecondApplicationPhaseEndDateError, setStartSecondApplicationPhaseEndDateError] =
    React.useState<boolean>(false);

  // state for the start date of the second application phase
  const onChangeStartDate = (date: Dayjs | null) => {
    setStartSecondApplicationPhaseStartDateInFutureError(false);
    setStartSecondApplicationPhaseStartBeforeEndError(false);
    setStartSecondApplicationPhaseEndDateError(false);
    if (date?.isBefore(dayjs())) {
      setStartSecondApplicationPhaseStartDateInFutureError(true);
    }

    if (startSecondApplicationPhaseEndDate && date?.isAfter(startSecondApplicationPhaseEndDate)) {
      setStartSecondApplicationPhaseStartBeforeEndError(true);
    }
    setStartSecondApplicationPhaseStartDate(date);
  };

  // state for the end date of the second application phase
  const onChangeEndDate = (date: Dayjs | null) => {
    setStartSecondApplicationPhaseEndDateError(false);
    setStartSecondApplicationPhaseStartBeforeEndError(false);
    if (date?.isBefore(dayjs())) {
      setStartSecondApplicationPhaseEndDateError(true);
    }

    if (startSecondApplicationPhaseStartDate && date?.isBefore(startSecondApplicationPhaseStartDate)) {
      setStartSecondApplicationPhaseEndDateError(true);
    }
    setStartSecondApplicationPhaseEndDate(date);
  };

  // handles the close of the start second application phase dialog
  const handleCloseStartSecondApplicationPhaseDialog = () => {
    setOpenStartSecondApplicationPhaseDialog(false);
    setStartSecondApplicationPhaseStartDateInFutureError(false);
    setStartSecondApplicationPhaseEndDateError(false);
    setStartSecondApplicationPhaseStartDate(null);
    setStartSecondApplicationPhaseEndDate(null);
  };

  // handles the click on the start second application phase button
  const handleStartSecondApplicationPhase = async () => {
    if (!startSecondApplicationPhaseStartDate || !startSecondApplicationPhaseEndDate) {
      return;
    }
    if (startSecondApplicationPhaseStartDate.isAfter(startSecondApplicationPhaseEndDate)) {
      setStartSecondApplicationPhaseStartDateInFutureError(true);
      return;
    }
    if (startSecondApplicationPhaseStartDate.isBefore(dayjs())) {
      setStartSecondApplicationPhaseStartDateInFutureError(true);
      return;
    }
    if (startSecondApplicationPhaseEndDate.isBefore(dayjs())) {
      setStartSecondApplicationPhaseEndDateError(true);
      return;
    }
    setStartSecondApplicationPhaseStartDateInFutureError(false);
    setStartSecondApplicationPhaseEndDateError(false);
    setOpenStartSecondApplicationPhaseDialog(false);
    try {
      await startSecondApplicationPhase({
        applicationStart2: startSecondApplicationPhaseStartDate.toDate(),
        applicationEnd2: startSecondApplicationPhaseEndDate.toDate(),
      });
    } catch (error) {
      // do nothing since the error is handled in the useProjectsDetails hook
    }
  };

  // state for the open withdraw application dialog
  const [openWithdrawApplicationDialog, setOpenWithdrawApplicationDialog] = useState(false);
  const [openCallOffProjectDialog, setOpenCallOffProjectDialog] = useState(false);

  const [staffingCommittee, setStaffingCommittee] = React.useState<MembersFieldDto[]>(
    projectDetails?.staffingCommittee || []
  );

  useEffect(() => {
    if (!projectDetails) return;
    if (isEditMode) return; // don’t clobber user edits
    setProjectKeyData(mapToProjectKeyData(projectDetails));
    setCustomerData(mapToCustomerData(projectDetails));
    setProjectDescriptionData(mapToProjectDescriptionData(projectDetails));
    setProjectCompletionNoteDraft(projectDetails.projectCompletionNote ?? "");
    setStaffingCommittee(projectDetails.staffingCommittee || []);
  }, [projectDetails, isEditMode]);

  // Reset all fields to the initial state
  const resetFields = () => {
    setProjectKeyData(mapToProjectKeyData(projectDetails));
    setProjectKeyDataErrors({
      projectName: false,
      estimatedProjectStart: false,
      estimatedProjectDuration: false,
      estimatedProjectEuroPerBT: false,
      estimatedProjectBTmin: false,
      estimatedProjectBTmax: false,
      estimatedProjectMemberMin: false,
      estimatedProjectMemberMax: false,
      applicationStart1: false,
      applicationEnd1: false,
      applicationStart2: false,
      applicationEnd2: false,
    });
    setCustomerData(mapToCustomerData(projectDetails));
    setCustomerDataErrors({
      customer: false,
      industry: false,
      newCustomer: false,
      contactPerson: false,
      acquisitor: false,
    });
    setProjectDescriptionData(mapToProjectDescriptionData(projectDetails));
    setProjectDescriptionDataErrors({
      coreCompetencies: false,
    });
  };

  // states for the active step and the completed steps
  const [activeStep, setActiveStep] = React.useState(0);

  // Get the total number of steps
  const totalSteps = () => {
    return steps.length;
  };

  // Check if the current step is the last step
  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  // Handle the saving of the project
  const handleSaveProject = () => {
    if (validateProjectKeyData(projectKeyDataErrors, projectKeyData, setProjectKeyDataErrors, projectDetails)) {
      showErrorMessage("Fehlerhafte oder fehlende Rahmendaten.");
      return;
    }
    if (validateCustomerData(customerDataErrors, customerData, setCustomerDataErrors)) {
      showErrorMessage("Fehlerhafte oder fehlende Kundendaten.");
      return;
    }
    if (
      validateProjectDescriptionData(
        projectDescriptionDataErrors,
        projectDescriptionData,
        setProjectDescriptionDataErrors
      )
    ) {
      showErrorMessage("Fehlerhafte oder fehlende Projektbeschreibung.");
      return;
    }
    // All data should be set at this point, but check again to be sure
    if (
      !projectKeyData.projectName ||
      !projectKeyData.estimatedProjectStart ||
      !projectKeyData.estimatedProjectDuration ||
      !projectKeyData.estimatedProjectEuroPerBT ||
      !projectKeyData.estimatedProjectBTmin ||
      !projectKeyData.estimatedProjectBTmax ||
      !projectKeyData.estimatedProjectMemberMin ||
      !projectKeyData.estimatedProjectMemberMax ||
      !customerData.acquisitor ||
      !customerData.acquisitionMethod ||
      !customerData.industry ||
      customerData.companyId === -1 ||
      !projectDescriptionData.coreCompetencies ||
      projectDescriptionData.coreCompetencies.length === 0 ||
      !customerData.contactPerson ||
      !projectKeyData.estimatedProjectEuroPerBT ||
      !projectKeyData.applicationEnd1
    ) {
      // Show an error message if some data is missing
      showErrorMessage("Einige Daten fehlen oder sind fehlerhaft");
      return;
    }
    const data: ProjectUpdateDto = {
      projectId: projectDetails?.projectId || Number(id),
      projectName: projectKeyData.projectName,
      status: projectDetails?.status as ProjectStatus,
      jobSite: projectKeyData.jobSite,
      tenderDate: projectDetails?.tenderDate || undefined,
      estimatedProjectStart: projectKeyData.estimatedProjectStart.toDate(),
      estimatedProjectDuration: projectKeyData.estimatedProjectDuration,
      estimatedProjectEuroPerBT: projectKeyData.estimatedProjectEuroPerBT,
      estimatedProjectEuroPerBTrange: projectKeyData.estimatedProjectEuroPerBTrange,
      estimatedProjectBTmin: projectKeyData.estimatedProjectBTmin,
      estimatedProjectBTmax: projectKeyData.estimatedProjectBTmax,
      estimatedProjectMemberMin: projectKeyData.estimatedProjectMemberMin,
      estimatedProjectMemberMax: projectKeyData.estimatedProjectMemberMax,
      applicationStart1: projectKeyData.applicationStart1 ? projectKeyData.applicationStart1.toDate() : null,
      applicationEnd1: projectKeyData.applicationEnd1 ? projectKeyData.applicationEnd1.toDate() : undefined,
      applicationStart2: projectKeyData.applicationStart2 ? projectKeyData.applicationStart2.toDate() : null,
      applicationEnd2: projectKeyData.applicationEnd2 ? projectKeyData.applicationEnd2.toDate() : null,
      situation: projectDescriptionData.situation,
      peculiarities: projectDescriptionData.peculiarities,
      coreCompetencies: projectDescriptionData.coreCompetencies,
      requirementProfile: projectDescriptionData.requirementProfile,
      referenceProjects: projectDescriptionData.referenceProjects,
      notes: projectDescriptionData.notes,
      acquisitor: customerData.acquisitor,
      acquisitionMethod: customerData.acquisitionMethod,
      contactPersons: customerData.contactPerson ? [customerData.contactPerson] : [],
      customerType: customerData.newCustomer ? "Neukunde" : "Altkunde",
      kickoff: projectKeyData.kickoff ? projectKeyData.kickoff.toDate() : null,
      client: {
        companyId: customerData.companyId,
        name: customerData.name,
        industry: customerData.industry,
        shortDescription: customerData.shortDescription,
        street: customerData.street,
        postalCode: customerData.postalCode,
        city: customerData.city,
        addressAdditional: customerData.addressAdditional,
        url: customerData.url,
        importantInformation: customerData.importantInformation,
        contactDesired: customerData.contactDesired,
        classified: customerData.classified,
      },
    };
    updateProject(data);
    setIsEditMode(false);
  };

  // Map current directors to MembersFieldDto
  const staffingDirectors = currentDirectors
    .filter(
      (director) =>
        director.directorId === 1 ||
        director.directorId === 2 ||
        director.directorId === 3 ||
        director.directorId === 4 ||
        director.directorId === 5
    )
    .map(
      (director) =>
        ({
          memberId: director.memberId,
          firstname: director.firstname,
          lastname: director.lastname,
        } as MembersFieldDto)
    );
  // Map members to MembersFieldDto
  const selectableStaffingCommittee = [
    ...members
      .filter((member) => member.memberStatus?.name === "Senior")
      .map(
        (member) =>
          ({
            memberId: member.memberId,
            firstname: member.firstname,
            lastname: member.lastname,
          } as MembersFieldDto)
      ),
    ...staffingDirectors,
  ];

  const [openStaffingCommittee, setOpenStaffingCommittee] = React.useState(false);

  const handleOpenStaffingCommitteeDialog = () => {
    setOpenStaffingCommittee(true);
  };

  const handleCloseStaffingCommitteeDialog = () => {
    setStaffingCommittee(projectDetails?.staffingCommittee || []);
    setOpenStaffingCommittee(false);
  };

  const handleSaveStaffingCommittee = () => {
    if (!staffingCommittee) {
      return;
    }
    const updatedStaffingCommittee = staffingCommittee.filter((member) => member.memberId !== undefined);
    setStaffingCommittee(updatedStaffingCommittee);
    saveStaffingCommittee(updatedStaffingCommittee);
    handleCloseStaffingCommitteeDialog();
  };

  // Function to handle the selection of a member
  const handleStaffingCommitteeSelection = (event: React.SyntheticEvent, value: MembersFieldDto | null) => {
    const index = Number(event.currentTarget.id.split("-")[1]);
    if (value !== null) {
      const updatedMembers = [...staffingCommittee];
      updatedMembers[index] = value;
      setStaffingCommittee(updatedMembers);
    }
  };

  // Function to add a member to the list of members
  const addStaffingCommitteeMember = () => {
    setStaffingCommittee((prev) => [...prev, [] as unknown as MembersFieldDto]);
  };

  // Function to remove a member from the list of members
  const removeStaffingCommitteeMember = (memberId: number) => {
    const updatedMembers = staffingCommittee.filter((member) => member.memberId !== memberId);
    setStaffingCommittee(updatedMembers);
  };

  // Handle the next step
  const handleNext = () => {
    const newActiveStep = isLastStep() ? 0 : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  // Handle the going back a step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle click on step
  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  // Render the current step
  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <KeyDataStep
            projectKeyData={projectKeyData}
            setProjectKeyData={setProjectKeyData}
            isEditMode={isEditMode}
            errors={projectKeyDataErrors}
            isProjectDetails
            projectStatus={projectDetails?.status || ""}
            staffingCommittee={projectDetails?.staffingCommittee || []}
          />
        );
      case 1:
        return (
          <CustomerStep
            customerData={customerData}
            setCustomerData={setCustomerData}
            isEditMode={isEditMode}
            errors={customerDataErrors}
          />
        );
      case 2:
        return (
          <ProjectDescriptionStep
            projectDescriptionData={projectDescriptionData}
            setProjectDescriptionData={setProjectDescriptionData}
            allCoreCompetencies={allCoreCompetencies}
            isEditMode={isEditMode}
            errors={projectDescriptionDataErrors}
          />
        );
      default:
        return (
          <KeyDataStep
            projectKeyData={projectKeyData}
            setProjectKeyData={setProjectKeyData}
            isEditMode={isEditMode}
            errors={projectKeyDataErrors}
          />
        );
    }
  };

  // handles the click on the edit button
  const handleEditProject = () => {
    // ensure draft starts with latest server values
    setProjectKeyData(mapToProjectKeyData(projectDetails));
    setCustomerData(mapToCustomerData(projectDetails));
    setProjectDescriptionData(mapToProjectDescriptionData(projectDetails));
    setIsEditMode(true);
  };

  // handles the click on the cancel button
  const handleCancelEdit = () => {
    resetFields();
    setIsEditMode(false);
  };

  // handles the click on the accepted button
  const handleOfferAccepted = async () => {
    await offerAccepted();
  };

  // handles the click on the rejected button
  const handleOfferRejected = async () => {
    await offerRejected();
  };

  // handles the click on the pitch lost button
  const handlePitchLost = async () => {
    await pitchLost();
  };

  // handles the click on the billing button
  const handleBilling = () => {
    navigate(`/projekte/${id}/projektabrechnung`);
  };

  // handles the click on the call off button
  const handleCallOffProject = async () => {
    setOpenCallOffProjectDialog(false);
    await callOffProject();
  };

  const handleSaveProjectCompletionNote = async () => {
    setIsSavingProjectCompletionNote(true);
    try {
      await updateProjectCompletionNote(projectCompletionNoteDraft);
    } finally {
      setIsSavingProjectCompletionNote(false);
    }
  };

  // handles the click on the apply button
  const handleApply = () => {
    navigate(`/projekte/projektbewerbung/${id}`);
  };

  // handles the click on the withdraw application button
  const handleWithdrawApplication = async () => {
    try {
      await withdrawApplication();
      setOpenWithdrawApplicationDialog(false);
    } catch (error) {
      // do nothing since the error is handled in the useProjectsDetails hook
    }
  };

  if (isProjectDetailsLoading) {
    return (
      <Container>
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ minHeight: "200px" }}>
          <LoadingCircle />
        </Stack>
      </Container>
    );
  }

  if (!projectDetails) {
    return (
      <Box>
        <Typography>Es konnte kein Projekt mit dieser ID gefunden werden</Typography>
      </Box>
    );
  }

  const savedProjectCompletionNote = projectDetails.projectCompletionNote ?? "";
  const projectClosureWaiverReason = projectDetails.projectClosureWaiverReason?.trim() ?? "";
  // The detail page mirrors the same Abschlussnotizen field as billing, but editing is only allowed in Abrechnung.
  const canEditProjectCompletionNote =
    projectDetails.status === "Abrechnung" && (hasProjectPermission || hasProjectBillingPermission || isPL);
  const isProjectCompletionNoteDirty = projectCompletionNoteDraft.trim() !== savedProjectCompletionNote.trim();

  return (
    <Container maxWidth="lg" sx={{ pb: 3 }}>
      <StartSecondApplicationPhaseDialog
        open={openStartSecondApplicationPhaseDialog}
        onClose={handleCloseStartSecondApplicationPhaseDialog}
        startDate={startSecondApplicationPhaseStartDate}
        endDate={startSecondApplicationPhaseEndDate}
        changeStartDate={onChangeStartDate}
        changeEndDate={onChangeEndDate}
        handleSubmit={handleStartSecondApplicationPhase}
        errorStartDateInFuture={startSecondApplicationPhaseStartDateInFutureError}
        errorStartBeforeEnd={startSecondApplicationPhaseStartBeforeEndError}
        errorEndDate={startSecondApplicationPhaseEndDateError}
      />
      <EditProjectStaffingCommitteeDialog
        open={openStaffingCommittee}
        onClose={handleCloseStaffingCommitteeDialog}
        handleStaffingCommitteeSelection={handleStaffingCommitteeSelection}
        addStaffingCommitteeMember={addStaffingCommitteeMember}
        removeStaffingCommitteeMember={removeStaffingCommitteeMember}
        staffingCommittee={staffingCommittee}
        selectableStaffingCommittee={selectableStaffingCommittee}
        handleSaveStaffingCommittee={handleSaveStaffingCommittee}
      />
      <ConfirmationDialog
        open={openWithdrawApplicationDialog}
        onClose={() => setOpenWithdrawApplicationDialog(false)}
        onConfirm={handleWithdrawApplication}
        cofirmButtonColor="error"
        title="Bewerbung zurückziehen"
        content="Bist du sicher, dass du deine Bewerbung zurückziehen möchtest? Diese Aktion kann nicht rückgängig gemacht werden."
        confirmButtonText="Zurückziehen"
      />
      <ConfirmationDialog
        open={openCallOffProjectDialog}
        onClose={() => setOpenCallOffProjectDialog(false)}
        onConfirm={handleCallOffProject}
        cofirmButtonColor="error"
        title="Projekt absagen"
        content={`Bist du sicher, dass du das Projekt ${projectDetails.projectName} absagen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmButtonText="Projekt absagen"
      />
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent={"space-between"}
        alignItems={isMobile ? "start" : "center"}
        sx={{ mb: 1 }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={4}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
            Projektdetails
          </Typography>
          <ProjectChip status={projectDetails.status} size={"medium"} />
        </Stack>
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent={"end"}
          sx={{ width: isMobile ? "100%" : "auto" }}
          gap={1}
        >
          {showBillingButton ? (
            <Button
              variant="outlined"
              color="success"
              sx={{ fontWeight: 600, mr: hasProjectPermission ? 2 : 0 }}
              onClick={handleBilling}
              startIcon={<Payments />}
              size="small"
            >
              Abrechnung
            </Button>
          ) : null}
          {hasProjectPermission ? (
            <>
              {!isEditMode ? (
                <ChangeStatusButtons
                  handleStartTendering={startTendering}
                  handleOfferAccepted={handleOfferAccepted}
                  handleOfferRejected={handleOfferRejected}
                  handlePitchLost={handlePitchLost}
                  handleCallOff={handleCallOffProject}
                  status={projectDetails.status}
                />
              ) : null}
              <EditButtons
                isEditMode={isEditMode}
                handleCancelEdit={handleCancelEdit}
                handleEditProject={handleEditProject}
                handleSaveProject={handleSaveProject}
              />
            </>
          ) : null}
        </Stack>
      </Stack>
      <Paper sx={{ paddingX: isMobile ? 2 : 4, paddingY: 2 }}>
        {isMobile ? (
          <>
            <Typography sx={{ fontWeight: "bold", fontSize: 24 }} gutterBottom>
              {steps[activeStep]}
            </Typography>
            <MobileStepper
              variant="text"
              steps={steps.length}
              activeStep={activeStep}
              position="static"
              sx={{ mt: -1, mb: 2 }}
              nextButton={
                <Button size="small" onClick={handleNext} disabled={activeStep === steps.length - 1}>
                  Weiter
                  {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                </Button>
              }
              backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                  {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                  Zurück
                </Button>
              }
            />
            <Box>{renderStep(activeStep)}</Box>
            <MobileStepper
              variant="text"
              steps={steps.length}
              activeStep={activeStep}
              position="static"
              sx={{ mt: 2 }}
              nextButton={
                <Button size="small" onClick={handleNext} disabled={activeStep === steps.length - 1}>
                  Weiter
                  {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                </Button>
              }
              backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                  {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                  Zurück
                </Button>
              }
            />
          </>
        ) : (
          <React.Fragment>
            <Stepper nonLinear activeStep={activeStep} sx={{ marginLeft: -1 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepButton color="inherit" onClick={handleStep(index)} sx={{ maxHeight: 2, borderRadius: 5 }}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            <React.Fragment>
              <Box sx={{ marginTop: 3 }}>{renderStep(activeStep)}</Box>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2, ml: -1 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  variant="outlined"
                >
                  Zurück
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button onClick={handleNext} sx={{ mr: 1 }} variant="outlined">
                  Weiter
                </Button>
              </Box>
            </React.Fragment>
          </React.Fragment>
        )}
      </Paper>
      {projectDetails.status === "Bewerbung" && isApplicationOpen ? (
        hasUserApplied ? (
          <Stack
            sx={{ mt: isMobile ? 4 : 2 }}
            direction={isMobile ? "column" : "row"}
            gap={3}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography>Du hast dich auf dieses Projekt beworben.</Typography>
            <Stack direction={"row"} gap={2}>
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={() => navigate(`/projekte/projektbewerbung/${id}`)}
              >
                Bewerbung überarbeiten
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setOpenWithdrawApplicationDialog(true)}
              >
                Bewerbung zurückziehen
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={isMobile ? "column" : "row"}
            gap={3}
            alignItems={"center"}
            sx={{ mt: isMobile ? 4 : 2 }}
            justifyContent={"space-between"}
          >
            <Typography>Du hast dich noch nicht auf dieses Projekt beworben.</Typography>
            <Button variant="contained" color="success" onClick={handleApply}>
              Jetzt bewerben
            </Button>
          </Stack>
        )
      ) : null}
      {projectDetails.status === "Bewerbung" && isUserInStaffingCommittee ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Groups />}
          onClick={() => navigate(`/projekte/${id}/projektbewerbungen`)}
          sx={{ mt: 2 }}
        >
          Projektbewerbungen anzeigen
        </Button>
      ) : null}
      {projectDetails.status === "Bewerbung" ? (
        hasProjectPermission && (
          <ProjectApplications
            applicants={projectDetails.members}
            handleStartSecondApplicationPhase={() => setOpenStartSecondApplicationPhaseDialog(true)}
            handleCallOffProject={() => setOpenCallOffProjectDialog(true)}
            isApplicationOpen={isApplicationOpen}
            isSecondApplicationPhase={isSecondApplicationPhase}
            handleOpenStaffingCommitteeDialog={handleOpenStaffingCommitteeDialog}
          />
        )
      ) : projectDetails.status === "NichtBesetzt" || projectDetails.status === "Lead" ? null : (
        <>
          <ProjectData
            projectMembers={projectDetails.members}
            qms={projectDetails.qms}
            euroPerBT={projectDetails.euroPerBT}
            signatureDate={projectDetails.signatureDate}
            soldBT={projectDetails.soldBT}
            soldExpenses={projectDetails.soldExpenses}
            projectId={projectDetails.projectId}
            hasProjectPermission={hasProjectPermission}
            isPL={isPL}
            updateProjectData={updateProjectData}
            checkHasPlQualification={checkHasPlQualification}
            checkHasQMQualification={checkHasQMQualification}
          />
          {projectDetails.status === "Abrechnung" || projectDetails.status === "Abgeschlossen" ? (
            <>
              <ProjectCompletionNoteCard
                note={projectCompletionNoteDraft}
                onChange={setProjectCompletionNoteDraft}
                onSave={canEditProjectCompletionNote ? handleSaveProjectCompletionNote : undefined}
                editable={canEditProjectCompletionNote}
                saveDisabled={!isProjectCompletionNoteDirty}
                saveLoading={isSavingProjectCompletionNote}
                minLength={PROJECT_COMPLETION_NOTE_MIN_LENGTH}
              />
              {projectClosureWaiverReason ? (
                <Card sx={{ mt: 4, paddingX: 4, paddingY: 2 }}>
                  <Typography variant="h6" component="h2" gutterBottom fontWeight={"bold"}>
                    Verzicht auf Folgeaktivität
                  </Typography>
                  <Divider sx={{ borderColor: "#f6891f", mb: 3 }} />
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Das Projekt wurde ohne geplante Folgeaktivität abgeschlossen.
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap" }}>{projectClosureWaiverReason}</Typography>
                  </Stack>
                </Card>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Container>
  );
};

export default ProjectDetails;
